import type { AnyExtension } from '@tiptap/core'
import { StarterKit, type StarterKitOptions } from '@tiptap/starter-kit'
import { deepmergeCustom, type DeepMergeLeafURI } from 'deepmerge-ts'
import { useMemo, type DependencyList } from 'react'
import {
  defaultDefinition,
  defaultStarterKitDefinition,
  type ExtensionOptions,
} from '../../extensions/definitions'
import type {
  EnabledExtensions,
  ExtensionsConfig,
  ExtensionsMergeConfig,
  UseExtensionsReturnValue,
} from './types'

export type { ExtensionsConfig, ExtensionsMergeConfig }

const isStarterKitExtension = <K extends keyof StarterKitOptions>(
  name: string | K
): name is K => name in defaultStarterKitDefinition

const isOwnProperty = (object: unknown, property: PropertyKey): boolean =>
  typeof object === 'object' &&
  object !== null &&
  Object.prototype.hasOwnProperty.call(object, property)

/**
 * Custom hook for TextEditor that provides a flexible way to load extensions,
 * with full type information.
 *
 * To merge extension configs, see `mergeExtensions` and `customMergeExtensions`.
 */
export const useExtensions = <T extends ExtensionOptions = ExtensionOptions>(
  configs: ExtensionsConfig<T>,
  deps: DependencyList = []
): UseExtensionsReturnValue<T> => {
  return useMemo(() => {
    const extensions: AnyExtension[] = []
    // Initialize `enabled` by setting all StarterKit extensions to enabled/true
    // StarterKit extensions will be set to false below as applicable
    // Imported extensions are not initialized here, only added later
    const enabled = Object.keys(defaultStarterKitDefinition).reduce<
      EnabledExtensions<T>
    >((enabled, name) => {
      enabled[name as keyof StarterKitOptions] = true
      return enabled
    }, {})

    Object.entries(configs).forEach(([key, ext]) => {
      if (ext.config !== false) {
        if (ext.extension) {
          enabled[key as keyof ExtensionOptions] = true
          const config = ext.config !== true ? ext.config : {}
          extensions.push((ext.extension as AnyExtension).configure(config))
        } else if (!isStarterKitExtension(key)) {
          console.warn(
            `[useExtensions] '${key}' is configured, but \`extension\` is missing. It will not be enabled.`
          )
        }
      }
    })

    const starterKitOptions = Object.entries(configs)
      .filter(([key]) => isStarterKitExtension(key))
      .reduce<Record<string, unknown>>((starterKit, [key, ext]) => {
        const config =
          ext.config !== undefined || ext.config !== true ? ext.config : {}
        if (config === false) {
          delete enabled[key as keyof StarterKitOptions]
        }

        starterKit[key] = config !== false && !ext.extension ? config : false
        return starterKit
      }, {}) as Partial<StarterKitOptions>

    extensions.unshift(StarterKit.configure(starterKitOptions))
    return [extensions, enabled]
  }, deps) // eslint-disable-line react-hooks/exhaustive-deps
}

export const customMergeExtensions = <T extends ExtensionOptions>(
  options: {
    /**
     * The source configuration. All imported extensions must
     * be defined in this config. If not present or undefined,
     * this defaults to the `defaultDefinition`.
     */
    source?: ExtensionsConfig<T>
    /**
     * When true, the merge operation takes care to respect the source
     * configuration. Extensions disabled in a prior config (excluding the
     * initial config) can't be re-enabled, and overrides for extensions not
     * present in the source will be ignored.
     */
    respectSource?: boolean
  },
  ...configs: ExtensionsMergeConfig<T>[]
): Readonly<ExtensionsConfig<T>> => {
  const source = options?.source ?? (defaultDefinition as ExtensionsConfig<T>)

  const customMergeFn = deepmergeCustom<
    {
      DeepMergeArraysURI: DeepMergeLeafURI
      DeepMergeOthersURI: 'KeyPathBasedMerge'
    },
    { keyPath: ReadonlyArray<PropertyKey> }
  >({
    enableImplicitDefaultMerging: true,
    // Accumulates the `keyPath` meta property
    metaDataUpdater: (prevMeta, curMeta) => {
      if (prevMeta === undefined) {
        return { keyPath: curMeta.key !== undefined ? [curMeta.key] : [] }
      }
      if (curMeta.key === undefined) {
        return prevMeta
      }
      return {
        ...curMeta,
        keyPath: [...prevMeta.keyPath, curMeta.key],
      }
    },
    mergeArrays: false,
    mergeOthers: (values, utils, meta) => {
      if (options?.respectSource === true) {
        const path = meta?.keyPath
        if (!path) {
          return
        }

        // If `false` is found in a config after the initial config,
        // don't merge any further values after `false`
        if (path.at(-1) === 'config' && values.length > 1) {
          const extensionName = path[0] as keyof T
          // `values` is not inclusive; if an object doesn't have a value,
          // there's no way to tell from looking at `values`. So, in order to
          // know if the first index below is the source value and should be
          // excluded, we need to check separately here if the source config
          // has a config value
          const sourceHasValue = isOwnProperty(source, extensionName)
            ? source[extensionName]?.config !== undefined
            : false
          const index = values.indexOf(false, sourceHasValue ? 1 : 0)
          if (index !== -1) {
            return utils.defaultMergeFunctions.mergeOthers(
              values.slice(0, index + 1)
            )
          }
          // If extension isn't in the source config and only receives an
          // override config (i.e. never has `extension` set), skip it
        } else if (path.length === 1 && !isOwnProperty(source, path[0])) {
          const final = utils.defaultMergeFunctions.mergeOthers(values) ?? {}
          if (typeof final === 'object' && !isOwnProperty(final, 'extension')) {
            return utils.actions.skip
          }
        }
      }
    },
  })

  return customMergeFn(source, ...configs) as ExtensionsConfig<T>
}

export const mergeExtensions = <T extends ExtensionOptions>(
  ...configs: ExtensionsMergeConfig<T>[]
): Readonly<ExtensionsConfig<T>> => customMergeExtensions<T>({}, ...configs)
